import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, getDocs, doc, updateDoc, addDoc, query, orderBy, deleteDoc } from 'firebase/firestore';

const AdminUsers = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [newUser, setNewUser] = useState({
    email: '',
    displayName: '',
    role: 'client',
    phone: ''
  });
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    admins: 0,
    clients: 0
  });

  useEffect(() => {
    loadUsers();
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const usersList = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        usersList.push({
          id: doc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate ? userData.createdAt.toDate() : userData.createdAt
        });
      });
      
      setUsers(usersList);
      calculateMetrics(usersList);
      console.log('Loaded users:', usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = (usersList) => {
    const metrics = {
      totalUsers: usersList.length,
      admins: usersList.filter(user => user.role === 'admin').length,
      clients: usersList.filter(user => user.role === 'client').length
    };
    setMetrics(metrics);
  };

  const deleteUserDoc = async (userId) => {
    try {
      const confirmDelete = window.confirm('Are you sure you want to delete this user record? This will remove the user document from Firestore but will NOT delete their Auth account.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'users', userId));
      const updated = users.filter(u => u.id !== userId);
      setUsers(updated);
      calculateMetrics(updated);
      alert('User record deleted successfully.');
    } catch (error) {
      console.error('Error deleting user document:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        isAdmin: newRole === 'admin',
        updatedAt: new Date()
      });
      
      // Update local state
      const updatedUsers = users.map(user => 
        user.id === userId 
          ? { ...user, role: newRole, isAdmin: newRole === 'admin', updatedAt: new Date() }
          : user
      );
      
      setUsers(updatedUsers);
      calculateMetrics(updatedUsers);
      console.log(`User ${userId} role updated to ${newRole}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role. Please try again.');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const userDoc = {
        email: newUser.email,
        displayName: newUser.displayName,
        role: newUser.role,
        isAdmin: newUser.role === 'admin',
        phone: newUser.phone || '',
        createdAt: new Date(),
        createdBy: currentUser.uid
      };

      const docRef = await addDoc(collection(db, 'users'), userDoc);
      
      // Add to local state
      const newUserWithId = {
        id: docRef.id,
        ...userDoc
      };
      
      const updatedUsers = [newUserWithId, ...users];
      setUsers(updatedUsers);
      calculateMetrics(updatedUsers);
      
      // Reset form and close modal
      setNewUser({ email: '', displayName: '', role: 'client', phone: '' });
      setShowAddUserModal(false);
      
      console.log('User added successfully:', newUserWithId);
    } catch (error) {
      console.error('Error adding user:', error);
      alert('Failed to add user. Please try again.');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#1b5e20';
      case 'client': return '#4caf50';
      default: return '#666';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'client': return 'Client';
      default: return role;
    }
  };

  return (
    <Layout>
      <div className="analytics-container admin-users-page" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: 'auto'
      }}>
        {/* Header */}
        <div style={{ 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            gap: windowWidth < 768 ? '1rem' : '0',
            maxWidth: '100%'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
                fontWeight: 'bold', 
                color: '#333', 
                margin: 0,
                wordBreak: 'break-word'
              }}>
                Manage Users
              </h1>
              <p style={{ 
                color: '#666', 
                marginTop: '0.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
                margin: '0.5rem 0 0 0'
              }}>
                Manage user accounts and permissions
              </p>
            </div>
            
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddUserModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: windowWidth < 768 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '0.875rem',
                whiteSpace: 'nowrap',
                width: windowWidth < 768 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <img 
                src="/customer.png" 
                alt="Add User" 
                style={{ 
                  width: windowWidth < 768 ? '18px' : '22px', 
                  height: windowWidth < 768 ? '18px' : '22px', 
                  objectFit: 'contain'
                }} 
              />
              Add New User
            </button>
          </div>
        </div>

        {/* Metrics Cards - HARDCODED ALL 3 CARDS */}
        <div className="stats-cards-container" style={{ 
          display: windowWidth <= 480 ? 'flex' : 'grid',
          flexDirection: windowWidth <= 480 ? 'column' : 'row',
          gridTemplateColumns: windowWidth <= 480 ? '1fr' : 
                               windowWidth <= 768 ? '1fr 1fr' : 
                               'repeat(3, 1fr)', 
          gap: '0.75rem',
          marginBottom: '2rem',
          padding: '1rem',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'visible',
          boxSizing: 'border-box',
          minHeight: windowWidth <= 480 ? '300px' : 'auto',
          height: 'auto'
        }}>
          {/* CARD 1: Total Users */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Total Users
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.totalUsers || 0}
            </div>
          </div>

          {/* CARD 2: Administrators */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Administrators
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.admins || 0}
            </div>
          </div>

          {/* CARD 3: Clients */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Clients
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {metrics.clients || 0}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card" style={{ 
          padding: 0, 
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#666', padding: '3rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <p>Loading users...</p>
            </div>
          ) : users.length > 0 ? (
            <>
              {windowWidth >= 768 ? (
                // Desktop Table Layout
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>User</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Contact</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Role</th>
                        <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Created</th>
                        <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr key={user.id} style={{ 
                          borderBottom: '1px solid #eee',
                          background: index % 2 === 0 ? 'white' : '#fafafa'
                        }}>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: getRoleColor(user.role),
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '0.875rem'
                              }}>
                                {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                  {user.displayName || 'No Name'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  ID: {user.id.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div>
                              <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                                {user.email}
                              </div>
                              {user.phone && (
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  📞 {user.phone}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <span style={{
                              background: getRoleColor(user.role),
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '16px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td style={{ padding: '1rem' }}>
                            <div style={{ fontSize: '0.875rem' }}>
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </div>
                          </td>
                          <td style={{ padding: '1rem', textAlign: 'center' }}>
                            <button
                              onClick={() => deleteUserDoc(user.id)}
                              style={{
                                background: 'none',
                                color: 'inherit',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                padding: '0.4rem 0.65rem',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                              title="Delete User Record"
                            >
                              🗑️ Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Mobile Card Layout
                <div style={{ 
                  padding: windowWidth <= 480 ? '0.75rem' : '1rem',
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '1rem'
                }}>
                  {users.map((user, index) => (
                    <div
                      key={user.id}
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: windowWidth <= 480 ? '1rem' : '1.25rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        boxSizing: 'border-box'
                      }}
                    >
                      {/* Mobile Card Header */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem',
                        marginBottom: '1rem'
                      }}>
                        <div style={{
                          width: windowWidth <= 480 ? '36px' : '40px',
                          height: windowWidth <= 480 ? '36px' : '40px',
                          borderRadius: '50%',
                          background: getRoleColor(user.role),
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: windowWidth <= 480 ? '0.8rem' : '0.875rem',
                          flexShrink: 0
                        }}>
                          {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: windowWidth <= 480 ? '0.9rem' : '1rem',
                            marginBottom: '0.25rem',
                            wordBreak: 'break-word'
                          }}>
                            {user.displayName || 'No Name'}
                          </div>
                          <div style={{ 
                            fontSize: windowWidth <= 480 ? '0.7rem' : '0.75rem', 
                            color: '#666'
                          }}>
                            ID: {user.id.substring(0, 8)}...
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <span style={{
                            background: getRoleColor(user.role),
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '16px',
                            fontSize: windowWidth <= 480 ? '0.7rem' : '0.75rem',
                            fontWeight: '600'
                          }}>
                            {getRoleLabel(user.role)}
                          </span>
                        </div>
                      </div>

                      {/* Mobile Card Content */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '0.75rem',
                        fontSize: windowWidth <= 480 ? '0.8rem' : '0.875rem'
                      }}>
                        <div>
                          <div style={{ 
                            fontWeight: '500', 
                            marginBottom: '0.25rem',
                            wordBreak: 'break-word'
                          }}>
                            📧 {user.email}
                          </div>
                          {user.phone && (
                            <div style={{ 
                              color: '#666',
                              fontSize: windowWidth <= 480 ? '0.75rem' : '0.8rem'
                            }}>
                              📞 {user.phone}
                            </div>
                          )}
                        </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          fontSize: windowWidth <= 480 ? '0.75rem' : '0.8rem',
                          color: '#666'
                        }}>
                          <div>
                            <strong>Created:</strong> {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                          </div>
                          <div>
                            <button
                              onClick={() => deleteUserDoc(user.id)}
                              style={{
                                background: 'none',
                                color: 'inherit',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                padding: windowWidth <= 480 ? '0.4rem 0.6rem' : '0.45rem 0.75rem',
                                cursor: 'pointer',
                                fontSize: windowWidth <= 480 ? '0.75rem' : '0.8rem'
                              }}
                              title="Delete User Record"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', padding: '3rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
              <h3 style={{ marginBottom: '0.5rem' }}>No Users Found</h3>
              <p>Users will appear here when they sign up or are added.</p>
            </div>
          )}
        </div>

        {/* Add User Modal */}
        {showAddUserModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: windowWidth < 768 ? '1rem 0.5rem' : '2rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: windowWidth < 768 ? '8px' : '12px',
              padding: windowWidth < 480 ? '1.5rem' : '2rem',
              maxWidth: windowWidth < 768 ? '100%' : '500px',
              width: windowWidth < 768 ? '100%' : '90%',
              maxHeight: windowWidth < 768 ? 'calc(100vh - 2rem)' : '80vh',
              overflowY: 'auto',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              margin: windowWidth < 768 ? '0' : 'auto'
            }}>
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1.5rem',
                borderBottom: '2px solid #eee',
                paddingBottom: '1rem'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  color: '#2e7d32', 
                  fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem',
                  wordBreak: 'break-word',
                  flex: 1,
                  marginRight: '1rem'
                }}>
                  Add New User
                </h2>
                <button 
                  onClick={() => setShowAddUserModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: windowWidth < 768 ? '1.25rem' : '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    flexShrink: 0
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Add User Form */}
              <form onSubmit={handleAddUser}>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                      placeholder="user@example.com"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={newUser.displayName}
                      onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                      Role *
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    >
                      <option value="client">Client</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    flexDirection: windowWidth < 480 ? 'column' : 'row',
                    gap: windowWidth < 480 ? '0.75rem' : '1rem', 
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #eee'
                  }}>
                    <button 
                      type="button"
                      onClick={() => setShowAddUserModal(false)}
                      style={{
                        background: '#666',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                        fontWeight: '600',
                        flex: 1,
                        width: windowWidth < 480 ? '100%' : 'auto'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      style={{
                        background: '#2e7d32',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
                        fontWeight: '600',
                        flex: 1,
                        width: windowWidth < 480 ? '100%' : 'auto'
                      }}
                    >
                      👤 Add User
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminUsers;